import { create } from 'axios';

import { getConfig } from 'src/config';
import { ENDPOINTS } from 'src/constants/api';
import { APP_ROUTES } from 'src/constants/routes';
import { clearAuthState } from 'src/features/auth/clearAuthState';
import { getLogoutRedirectTarget } from 'src/features/auth/logoutRedirectTarget';
import { refreshToken } from 'src/features/auth/tokenRefresher';

import type { AxiosRequestConfig } from 'axios';

// Internal interceptor flags — stored on the Axios request config, never sent
// as HTTP headers so they cannot trigger a CORS preflight.
declare module 'axios' {
  interface AxiosRequestConfig {
    _retried?: boolean;
    _skipRedirect?: boolean;
  }
  interface InternalAxiosRequestConfig {
    _retried?: boolean;
    _skipRedirect?: boolean;
  }
}

// Module-level latch so a terminal auth failure — even across N concurrent
// requests that all resolve to the same single-flight refresh — triggers exactly
// one clearAuthState() + redirect, never a stampede of navigations.
let _loggingOut = false;

type TerminalLogoutSource =
  | 'reactive_interceptor'
  | 'auth0_message_check'
  | '401_interceptor'
  | 'no_response_interceptor'
  | 'entry_gate';

// Exported so the entry-time session freshness gate (useSessionFreshnessGate)
// can reuse this exact single-fire logout + telemetry instead of duplicating it.
export function terminalLogout(
  requestConfig?: AxiosRequestConfig,
  reason?: string,
  source: TerminalLogoutSource = 'reactive_interceptor',
) {
  if (requestConfig?._skipRedirect) return;
  if (_loggingOut) return;
  _loggingOut = true;
  window.FS?.('trackEvent', {
    name: 'auth_terminal_logout',
    properties: {
      reason: reason ?? 'unknown_terminal',
      source,
      url: requestConfig?.url ?? null,
    },
  });
  clearAuthState();
  window.location.href = getLogoutRedirectTarget();
}

export type ApiError = {
  title: string;
  detail: string;
  status: number;
};

export type ResetResponse = {
  detail: string;
  instance: string;
  status: number;
  title: string;
  type: string;
};

export const getApiClient = async ({
  config = {},
  token,
}: {
  config?: AxiosRequestConfig;
  token?: string;
} = {}) => {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization =
      token?.indexOf('Bearer') !== -1 ? token : `Bearer ${token}`;
  }

  const instance = create({
    baseURL: getConfig().API.url,
    headers: {
      ...headers,
      ...(config.headers ?? {}),
    },
    ...Object.fromEntries(
      Object.entries(config).filter(([key]) => key !== 'headers'),
    ),
  });

  instance.interceptors.response.use(
    (resp) => {
      return resp;
    },
    async (error) => {
      const { response } = error;
      const requestConfig = error.config;
      // Auth-failure handling (refresh, terminal logout, 503 page) only applies
      // to requests that carried a token. Unauthenticated callers — guest /
      // consent flows — have no session to refresh or end, and must never be
      // navigated away from the page they're on.
      const hasAuthHeader = Boolean(requestConfig?.headers?.['Authorization']);

      console.error('API Error:', {
        url: requestConfig?.url,
        method: requestConfig?.method,
        status: response?.status,
        statusText: response?.statusText,
        data: response?.data,
      });

      if (response) {
        const { data, status } = response;

        // Auth0-path terminal signal (refresh token expired server-side). Route
        // through the same single-fire latch as the union-based terminal path.
        if (
          hasAuthHeader &&
          data?.message?.includes('Refresh Token has expired')
        ) {
          terminalLogout(requestConfig, 'auth0_refresh_token_expired', 'auth0_message_check');
          return Promise.reject(error);
        }

        if (
          status === 401 &&
          hasAuthHeader &&
          requestConfig &&
          !requestConfig._retried &&
          !requestConfig.url?.includes(ENDPOINTS.companionTokenRefresh)
        ) {
          // One refresh path for both auth modes: the registered refresher
          // (Auth0 silent refresh OR companion handle refresh) decides, and the
          // shared single-flight collapses concurrent 401s into one call.
          const outcome = await refreshToken();

          switch (outcome.status) {
            case 'success':
              requestConfig._retried = true;
              requestConfig.headers.Authorization = `Bearer ${outcome.accessToken}`;
              return instance.request(requestConfig);
            case 'transient':
              // Recoverable (5xx/network/lock): do NOT log out — let the caller
              // surface the error and retry later.
              return Promise.reject(error);
            case 'terminal':
              terminalLogout(requestConfig, outcome.reason, '401_interceptor');
              return Promise.reject(error);
          }
        }

        // Redirect based on error status code
        if (
          status === 503 &&
          hasAuthHeader &&
          window.location.pathname !== APP_ROUTES.CONSENT &&
          !requestConfig?._skipRedirect
        ) {
          window.location.href = APP_ROUTES.SERVICE_UNAVAILABLE;
        }
      }

      // API Gateway Lambda authorizers return 401 before the Lambda runs, so no
      // CORS headers are added. The browser blocks the response → Axios sees a
      // Network Error with no `response`. Treat an authenticated request with no
      // response the same as a visible 401: attempt one token refresh.
      if (
        !response &&
        requestConfig &&
        !requestConfig._retried &&
        hasAuthHeader &&
        !requestConfig.url?.includes(ENDPOINTS.companionTokenRefresh)
      ) {
        const outcome = await refreshToken();

        switch (outcome.status) {
          case 'success':
            requestConfig._retried = true;
            requestConfig.headers.Authorization = `Bearer ${outcome.accessToken}`;
            return instance.request(requestConfig);
          case 'transient':
            return Promise.reject(error);
          case 'terminal':
            terminalLogout(requestConfig, outcome.reason, 'no_response_interceptor');
            return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    },
  );

  return instance;
};
